import { useState } from "react";
import { Phone, Heart, ShieldCheck, Eye, Moon, Smartphone } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";

const CATEGORIES = [
  {
    id: "online", label: "Online behaviour", icon: Smartphone,
    signs: ["Hides the screen or switches apps quickly when you come near", "New accounts you weren't told about", "Very late-night messaging", "Strong reactions when asked to put the phone down"],
  },
  {
    id: "mood", label: "Mood shifts", icon: Moon,
    signs: ["Lasting sadness or irritability beyond a couple of weeks", "Pulling away from friends or activities they loved", "Big changes in sleep or appetite", "Talking about feeling worthless or hopeless"],
  },
  {
    id: "secrecy", label: "Secrecy & withdrawal", icon: Eye,
    signs: ["Becoming secretive about where they go or who they talk to", "A new friend or group you know nothing about", "Money or items appearing or disappearing", "Skipping school or dropping grades suddenly"],
  },
];

const HELPLINES = [
  { region: "US & Canada", name: "988 Suicide & Crisis Lifeline", contact: "Call or text 988" },
  { region: "UK & ROI", name: "Samaritans", contact: "116 123 (free, 24/7)" },
  { region: "India", name: "iCall / Kiran", contact: "+91 9152987821 · 1800-599-0019" },
  { region: "Australia", name: "Lifeline", contact: "13 11 14" },
];

export default function Safety() {
  const [active, setActive] = useState("online");
  const cat = CATEGORIES.find((c) => c.id === active);
  return (
    <div>
      <PublicNav />
      <section className="dk-container py-16" data-testid="safety-checklist-container">
        <div className="max-w-2xl mb-8">
          <span className="badge-soft mb-3"><ShieldCheck size={14} /> Always free · always open</span>
          <h1 className="text-[32px] sm:text-[40px] font-semibold">Warning-sign checklists</h1>
          <p className="mt-3 text-[18px]" style={{ color: "var(--text-muted)" }}>
            A calm, practical guide to changes worth noticing. Every child is different — these are gentle prompts, not diagnoses. Trust what you know about your own child.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)} data-testid={`safety-tab-${c.id}`}
              className={`btn ${active === c.id ? "btn-secondary" : "btn-outline"}`}>
              <c.icon size={16} /> {c.label}
            </button>
          ))}
        </div>

        <div className="dk-card p-8 mb-10">
          <h2 className="font-serif text-[22px] font-medium mb-5">{cat.label} — signs to notice gently</h2>
          <ul className="space-y-3">
            {cat.signs.map((s, i) => (
              <li key={i} className="flex gap-3 items-start rounded-xl p-4" style={{ background: "var(--card-alt)" }}>
                <span className="grid place-items-center w-6 h-6 rounded-full shrink-0 mt-0.5" style={{ background: "var(--sage-tint)", color: "var(--sage)" }}>{i + 1}</span>
                <span className="text-[15px]">{s}</span>
              </li>
            ))}
          </ul>
          <div className="rounded-xl p-4 mt-6 flex gap-3" style={{ background: "var(--sage-tint)" }}>
            <Heart size={20} style={{ color: "var(--sage)" }} className="shrink-0 mt-0.5" />
            <p className="text-sm">Noticing a few of these doesn't mean something is wrong. Start with warmth and curiosity — a calm “I've noticed you seem a bit quiet, I'm here whenever you want to talk” goes a long way.</p>
          </div>
        </div>

        <h2 className="font-serif text-[24px] font-medium mb-4">If you need to talk to someone now</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {HELPLINES.map((h) => (
            <div key={h.region} className="dk-card p-5 space-y-2" data-testid="safety-helpline-card">
              <span className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{h.region}</span>
              <h3 className="font-sans font-semibold">{h.name}</h3>
              <p className="flex items-center gap-2 text-sm" style={{ color: "var(--accent-strong)" }}><Phone size={14} /> {h.contact}</p>
            </div>
          ))}
        </div>
        <p className="text-xs mt-6" style={{ color: "var(--text-muted)" }}>Helplines verified periodically. In an emergency, always call your local emergency number first.</p>
      </section>
      <Footer />
    </div>
  );
}
